/**
 * subscriptionCancelAdapter — le parcours « Résilier l'abonnement », des valeurs de fil aux
 * formes `UICancel*`.
 *
 * Port littéral de `src/student/account/subscription/cancel-flow/` : les six motifs de
 * `lib/flow.js:17-24`, la table des alternatives de `CancelAlternativesScreen.js:32-126`, les
 * dérivations de `CancelFeesScreen.js:28-207`, de `CancelPaymentScreen.js:33-49` et `:62-89`,
 * `:252-356`, `:389-411`, et celles de `CancelGoodbyeScreen.js:17-93`.
 *
 * C'est ici que tout montant, toute date et toute phrase datée s'écrivent : le design system n'en
 * calcule aucun. Les clés (`motif`, `outcome`) sont des valeurs d'API — elles partent telles
 * quelles dans `POST /api/v1/subscription/cancel` (`ChurnIntent::MOTIFS` côté back), d'où leur
 * place dans un adaptateur plutôt que dans un formatter.
 *
 * Jamais importé par le DS.
 */
import { formatDayMonth, formatDayMonthYear } from "../../../ui/formatters/dateFormatter"
import { eurosCents } from "../../../ui/formatters/moneyFormatter"
import {
    type UICancelAlternative,
    type UICancelFees,
    type UICancelPaymentMethod,
    type UICancelPaymentNotice,
    type UICancelPaymentStatus,
    type UICancelReason,
    type UICancelSummaryRow,
} from "../../../ui/types"

// ─────────────────────────────────────────────────────────────────────────────
// Formes servies par le backend (toutes les clés facultatives — règle des adaptateurs)
// ─────────────────────────────────────────────────────────────────────────────

/** Le moyen de paiement par défaut, tel que `CancelSubscriptionController::preview` le normalise. */
export type ApiDefaultPaymentMethod = {
    brand?: string | null
    last4?: string | null
    exp_month?: number | string | null
    exp_year?: number | string | null
    available?: boolean | null
}

/**
 * `GET /api/v1/subscription/cancellation-fees` — la réponse est enveloppée dans un
 * `JsonResource`, donc `state.api.getCancellationFees.data` porte déjà l'objet ci-dessous.
 */
export type ApiCancellationFees = {
    fees_cents?: number | null
    /**
     * Le crédit de la famille déduit des frais, ce que la carte paie, et le crédit au-delà des
     * frais (non remboursé). Absents d'un backend plus ancien : rien n'est déduit.
     */
    credit_applied_cents?: number | null
    /** Un solde que la famille DOIT : Stripe l'ajoute aux frais, l'écran le dit. */
    balance_due_cents?: number | null
    amount_due_cents?: number | null
    credit_left_cents?: number | null
    commitment_ends_at?: string | null
    remaining_months?: number | null
    /** `null` sur une résiliation de jalon : il n'y a pas de service en cours à porter. */
    end_of_month?: string | null
    milestone_cancellation?: boolean | null
    resume_date?: string | null
    /** La famille est dans son premier mois sans engagement : `fees_cents` vaut alors 0. */
    first_month_without_commitment?: boolean | null
    /** `Y-m-d` ; `null` tant que le premier cours n'a pas eu lieu. */
    first_month_ends_on?: string | null
    default_pm?: ApiDefaultPaymentMethod | null
    parent_email?: string | null
    parent_first_name?: string | null
    student_first_name?: string | null
}

/** Le `UserResource` rafraîchi que rend `POST /api/v1/subscription/cancel`. */
export type ApiCancelSubscriptionResult = {
    parent_email?: string | null
    subscription_state?: { end_date?: string | null } | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Montants et dates
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Le prix d'un mois d'engagement restant, affiché sous le montant des frais. Écrit en dur comme
 * dans `CancelFeesScreen.js:324` : le backend calcule la somme
 * (`PaymentDunningService::calculateTerminationFine`) mais ne sert pas le prix unitaire.
 */
const MONTHLY_COMMITMENT_PRICE_LABEL = eurosCents(1990)

/**
 * `formatLongDate` des trois écrans — et son silence sur une date absente : le legacy rend `""`,
 * jamais « Invalid date ». `padded` distingue les deux formats du parcours : les frais écrivent
 * « 13 août 2026 » sur deux chiffres (`CancelFeesScreen.js:38-42`), le paiement et l'accusé sur
 * un seul (`CancelPaymentScreen.js:43-47`, `CancelGoodbyeScreen.js:27-31`).
 */
function longDate(iso: string | null | undefined, padded: boolean): string {
    if (!iso) return ""
    return formatDayMonthYear(iso, padded)
}

/** `CancelPaymentScreen.js:49`. */
function pad2(value: number | string | null | undefined): string {
    return String(value ?? "").padStart(2, "0")
}

/** `milestone_cancellation` traverse les paramètres de route, où il peut devenir une chaîne. */
export function isMilestoneCancellation(value: unknown): boolean {
    return value === true || value === "true"
}

// ─────────────────────────────────────────────────────────────────────────────
// Étape 1 — les motifs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Les six motifs, dans l'ordre de `lib/flow.js:17-24`. « Autre » porte `requiresDetail` : le
 * legacy teste `motif === "other"` (`CancelReasonScreen.js:20`), la page pose la question sur le
 * motif et non sur sa clé.
 */
export const CANCEL_REASONS: UICancelReason[] = [
    { key: "too_expensive", label: "C'est trop cher en ce moment" },
    { key: "no_progress", label: "On ne voit pas de progrès" },
    { key: "child_disengaged", label: "Mon enfant ne veut plus" },
    { key: "tutor_mismatch", label: "Le tuteur ne convient pas" },
    { key: "no_time", label: "Mon enfant n'a plus le temps" },
    { key: "other", label: "Autre (préciser)", requiresDetail: true },
]

/** `lib/flow.js:26-29` — chaîne vide quand la clé n'est pas des nôtres. */
export function cancelReasonLabel(motif: string | null | undefined): string {
    return CANCEL_REASONS.find((reason) => reason.key === motif)?.label ?? ""
}

/** Le motif exige-t-il une précision écrite ? (legacy : `motif === "other"`) */
export function cancelReasonRequiresDetail(motif: string | null | undefined): boolean {
    return CANCEL_REASONS.find((reason) => reason.key === motif)?.requiresDetail === true
}

// ─────────────────────────────────────────────────────────────────────────────
// Étape 2 — l'alternative et ses gestes
// ─────────────────────────────────────────────────────────────────────────────

/** Quel rendez-vous le lien Calendly annonce — `lib/calendly.js:4-9`. */
export type CancelCalendlyVariant = "educator" | "child_weekend" | "advisor"

/**
 * Ce que fait une action de l'alternative — la part que le DS ne porte pas
 * (`UICancelAlternativeAction` n'a que de quoi dessiner un bouton).
 *
 * `CancelAlternativesScreen.js:20-31` : trois natures, toutes existantes.
 * `intent` n'est pas repris : le portail ne lit que `openSupport` / `triggerPause`
 * (`ParentSubscriptionPortalScreen.js:298-312`), `supportIntent` et `supportMotif` sont morts.
 */
export type CancelAlternativeActionSpec =
    { kind: "calendly"; variant: CancelCalendlyVariant } | { kind: "support_modal" } | { kind: "pause_flow" }

/** La nature de chaque action, par clé — `CancelAlternativesScreen.js:32-123`. */
export const CANCEL_ALTERNATIVE_ACTIONS: Record<string, CancelAlternativeActionSpec> = {
    autonomy_request: { kind: "support_modal" },
    no_progress_calendly: { kind: "calendly", variant: "educator" },
    child_weekend_calendly: { kind: "calendly", variant: "child_weekend" },
    tutor_change_request: { kind: "support_modal" },
    no_time_reduce: { kind: "support_modal" },
    no_time_pause: { kind: "pause_flow" },
    other_calendly: { kind: "calendly", variant: "advisor" },
}

/**
 * Les six alternatives, texte pour texte. Deux icônes n'ont pas leur équivalent exact au registre
 * et prennent la voisine, décision du lot des pages : `comments` → `comment-dots`,
 * `calendar-week` → `calendar-alt`. `mode: "outlined"` devient `variant: "outline"`.
 */
const CANCEL_ALTERNATIVES_BY_REASON: Record<string, UICancelAlternative> = {
    too_expensive: {
        iconName: "graduation-cap",
        title: "Passer en Autonomie à 19 €/mois",
        pitch: "Gardez l'accès à Masteur pour votre enfant à petit prix : énoncés et cours illimités, plus 1 cours par mois avec un tuteur. Idéal pour souffler côté budget sans rompre le suivi.",
        actions: [{ key: "autonomy_request", label: "Demander le passage en Autonomie", iconName: "exchange-alt" }],
    },
    no_progress: {
        iconName: "chart-line",
        title: "Faire un point pédagogique avant de décider",
        pitch: "Un responsable pédagogique reprend l'historique des séances avec vous, identifie ce qui bloque et ajuste l'accompagnement. Beaucoup de familles repartent avec un plan clair après ce RDV.",
        actions: [
            {
                key: "no_progress_calendly",
                label: "Prendre RDV avec un conseiller pédagogique",
                iconName: "calendar-check",
            },
        ],
    },
    child_disengaged: {
        iconName: "child",
        title: "Un RDV gratuit avec un pédagogue pour votre enfant",
        pitch: "Souvent, c'est une motivation à raviver plus qu'un blocage de fond. On propose à votre enfant un échange court, dédié et bienveillant un week-end pour comprendre ce qui coince et réenclencher l'envie.",
        actions: [{ key: "child_weekend_calendly", label: "Réserver un RDV week-end", iconName: "calendar-alt" }],
    },
    tutor_mismatch: {
        iconName: "user-friends",
        title: "Changer de tuteur",
        pitch: "Le bon tuteur change tout. Demandez un nouveau matching : on vous propose un autre profil aligné avec votre enfant sous 48h, sans interruption d'abonnement.",
        actions: [{ key: "tutor_change_request", label: "Demander un changement de tuteur", iconName: "exchange-alt" }],
    },
    no_time: {
        iconName: "clock",
        title: "Adapter l'abonnement plutôt que de résilier",
        pitch: "Si l'agenda déborde temporairement, deux options pour garder Masteur dans la durée : réduire le volume hebdo ou suspendre l'abonnement jusqu'à 1 mois.",
        actions: [
            { key: "no_time_reduce", label: "Réduire mes heures", iconName: "minus-circle" },
            { key: "no_time_pause", label: "Faire une pause longue", iconName: "pause-circle", variant: "outline" },
        ],
    },
    other: {
        iconName: "comment-dots",
        title: "Un RDV avec un conseiller pour en parler",
        pitch: "Avant de résilier, prenez 15 minutes avec nous pour qu'on comprenne votre besoin précis et qu'on propose la meilleure suite — ajustement, pause, ou résiliation en bonne intelligence.",
        actions: [{ key: "other_calendly", label: "Prendre RDV avec un conseiller", iconName: "calendar-check" }],
    },
}

/** `ALTERNATIVES_BY_MOTIF[motif] ?? DEFAULT_ALTERNATIVE` — le repli est « Autre ». */
export function cancelAlternativeFor(motif: string | null | undefined): UICancelAlternative {
    return CANCEL_ALTERNATIVES_BY_REASON[motif ?? ""] ?? CANCEL_ALTERNATIVES_BY_REASON.other
}

/** `lib/calendly.js:4-9` — le préfixe qui nomme le rendez-vous dans le champ pré-rempli. */
function calendlyVariantPrefix(variant: CancelCalendlyVariant): string {
    if (variant === "educator") return "RDV pédagogique"
    if (variant === "child_weekend") return "RDV week-end enfant"
    return "RDV conseiller"
}

/** `openCancelCalendly` sans `motifLabel` — `lib/calendly.js:19-27`. */
export function cancelCalendlyLabel(motif: string | null | undefined): string | null {
    const label = cancelReasonLabel(motif)
    return label ? `Demande de résiliation — ${label}` : null
}

/** `openCancelCalendlyForReason` — `lib/calendly.js:29-36`. */
export function cancelCalendlyLabelForVariant(
    motif: string | null | undefined,
    variant: CancelCalendlyVariant
): string {
    const label = cancelReasonLabel(motif)
    const prefix = calendlyVariantPrefix(variant)
    return label ? `${prefix} — résiliation (${label})` : `${prefix} — résiliation`
}

/** Le repli de `handleCtaPress` quand une action n'a pas de nature — `CancelAlternativesScreen.js:184-190`. */
export function cancelCalendlyFallbackLabel(motif: string | null | undefined): string {
    return `RDV conseiller — résiliation (${cancelReasonLabel(motif)})`
}

// ─────────────────────────────────────────────────────────────────────────────
// Étape 3 — les frais
// ─────────────────────────────────────────────────────────────────────────────

type CancelFeesCredit = { appliedCents: number; balanceDueCents: number; dueCents: number; leftCents: number }

/**
 * Le crédit de la famille sur les frais, tel que le backend le calcule (Stripe l'impute de lui-même
 * sur la facture des frais). Un backend qui ne le sert pas encore : rien de déduit, tout est dû.
 */
export function cancelFeesCredit(preview: ApiCancellationFees | null | undefined): CancelFeesCredit {
    const feesCents = preview?.fees_cents ?? 0
    return {
        appliedCents: preview?.credit_applied_cents ?? 0,
        balanceDueCents: preview?.balance_due_cents ?? 0,
        dueCents: preview?.amount_due_cents ?? feesCents,
        leftCents: preview?.credit_left_cents ?? 0,
    }
}

/** Les lignes du solde sous le montant ; le crédit au-delà des frais n'est pas remboursé. */
function cancelFeesCreditLines(feesCents: number, credit: CancelFeesCredit): string[] {
    if (credit.appliedCents <= 0 && credit.balanceDueCents <= 0) return []
    let breakdown = `Frais ${eurosCents(feesCents)}`
    if (credit.appliedCents > 0) breakdown += ` − crédit ${eurosCents(credit.appliedCents)}`
    if (credit.balanceDueCents > 0) breakdown += ` + solde dû ${eurosCents(credit.balanceDueCents)}`
    const lines = [breakdown]
    if (credit.leftCents > 0) {
        lines.push(`Les ${eurosCents(credit.leftCents)} de crédit restants ne sont pas remboursés.`)
    }
    return lines
}

/** `CancelFeesScreen.js:118-207` et le corps qu'il rend. `null` tant que la réponse n'est pas là. */
export function toUICancelFees(preview: ApiCancellationFees | null | undefined): UICancelFees | null {
    if (preview == null) return null

    // Le serveur décide du premier mois et met déjà les frais à 0 : on affiche son montant tel quel.
    const isFirstMonth = !!preview.first_month_without_commitment
    const feesCents = preview.fees_cents ?? 0
    const credit = cancelFeesCredit(preview)
    // « Des frais » = quelque chose à prélever : un crédit qui couvre tout ouvre le chemin sans
    // paiement, comme une résiliation gratuite.
    const hasFees = credit.dueCents > 0
    const coveredByCredit = !hasFees && credit.appliedCents > 0
    const isMilestone = !!preview.milestone_cancellation
    const resumeDate = preview.resume_date
    const endOfMonth = preview.end_of_month
    const childName = preview.student_first_name || "votre enfant"

    // « Reprise prévue le … » ne remplace la fin d'engagement que sur un jalon SANS frais : un
    // jalon qui porte un engagement hérité garde sa vraie date de fin d'engagement.
    const showsResumeDate = isMilestone && !hasFees

    const noFeesReason = isMilestone
        ? "Reprise non commencée"
        : isFirstMonth
          ? `Vous êtes dans votre 1er mois sans engagement : aucun frais de résiliation${
                preview.first_month_ends_on
                    ? ` (jusqu'au ${formatDayMonth(preview.first_month_ends_on, "numeric")})`
                    : ""
            }.`
          : "Engagement terminé"

    return {
        hasFees,
        amountLabel: eurosCents(credit.dueCents),
        title: hasFees
            ? "Voici les frais associés à votre résiliation"
            : coveredByCredit
              ? "Votre crédit couvre les frais de résiliation"
              : "Aucun frais — votre résiliation est gratuite",
        lead: coveredByCredit
            ? "Vous êtes encore sous engagement contractuel : le crédit de votre compte règle les frais, rien ne sera prélevé."
            : hasFees
              ? `Vous êtes encore sous engagement contractuel. Le montant correspond aux mois restants${
                    credit.appliedCents > 0 ? ", moins le crédit de votre compte" : ""
                }.`
              : isMilestone
                ? "Votre reprise n'a pas encore commencé : elle s'annule sans frais."
                : isFirstMonth
                  ? "Vous pouvez résilier sans frais."
                  : "Aucun engagement en cours : vous pouvez résilier sans frais.",
        detail:
            feesCents > 0
                ? `${preview.remaining_months} mois d'engagement restants × ${MONTHLY_COMMITMENT_PRICE_LABEL}`
                : noFeesReason,
        creditLines: cancelFeesCreditLines(feesCents, credit),
        dateLine: showsResumeDate
            ? `Reprise prévue le ${longDate(resumeDate, true)}`
            : isFirstMonth
              ? null
              : `Fin d'engagement initialement prévue le ${longDate(preview.commitment_ends_at, true)}`,
        effectTitle: isMilestone ? "Prise d'effet immédiate" : "Prise d'effet à la fin du mois",
        effectText: isMilestone
            ? `La reprise prévue le ${longDate(resumeDate, true)} est annulée dès confirmation : aucun cours ne sera planifié et aucun prélèvement d'abonnement n'aura lieu.`
            : `Votre résiliation prendra effet à la fin du mois en cours${endOfMonth ? ` (le ${longDate(endOfMonth, true)})` : ""}. ${childName} garde l'accès à ses lives jusqu'à cette date.`,
    }
}

/** La phrase de la confirmation sans frais — `CancelFeesScreen.js:167-169`. */
export function cancelFeesConfirmMessage(preview: ApiCancellationFees | null | undefined): string {
    if (preview?.milestone_cancellation) {
        return `Cette action annule la reprise prévue le ${longDate(preview.resume_date, true)}. Aucun prélèvement n'aura lieu, et vous pourrez revenir quand vous voulez.`
    }
    return `Cette action mettra fin à votre abonnement le ${longDate(preview?.end_of_month, true)}. Vous pourrez revenir quand vous voulez.`
}

// ─────────────────────────────────────────────────────────────────────────────
// Étape 4 — le paiement
// ─────────────────────────────────────────────────────────────────────────────

/** Ce que l'étape 4 reçoit de l'étape 3, par les paramètres de route (`CancelFeesScreen.js:148-158`). */
export type CancelPaymentContext = {
    motif?: string | null
    feesCents?: number | null
    /** Le crédit déduit des frais (étape 3) ; la carte paie le reste. */
    creditCents?: number | null
    /** Un solde que la famille doit, ajouté aux frais. */
    balanceDueCents?: number | null
    /** Ce que la carte paie, tel que le backend l'a calculé (`cancelFeesCredit`). */
    dueCents?: number | null
    endOfMonth?: string | null
    milestoneCancellation?: unknown
    resumeDate?: string | null
}

/** `CancelPaymentScreen.js:360-411` — `null` quand aucune carte n'est enregistrée. */
export function toUICancelPaymentMethod(pm: ApiDefaultPaymentMethod | null | undefined): UICancelPaymentMethod | null {
    if (!pm?.last4) return null
    // Les deux replis du legacy diffèrent — « Carte » pour l'initiale, « carte » pour la suite —
    // et donnent « Carte » quand la marque manque. Recopié tel quel.
    const brandLabel = (pm.brand ?? "Carte").charAt(0).toUpperCase() + (pm.brand ?? "carte").slice(1)
    return {
        label: `${brandLabel} •••• ${pm.last4}`,
        detail: `Expire ${pad2(pm.exp_month)}/${pm.exp_year} · Moyen de paiement par défaut`,
    }
}

/** La phrase d'accès, sous le titre de l'étape 4 — `CancelPaymentScreen.js:267-291`. */
export function toUICancelAccessText(context: CancelPaymentContext): string {
    if (isMilestoneCancellation(context.milestoneCancellation)) {
        return `La reprise prévue le ${longDate(context.resumeDate, false)} est annulée dès le paiement.`
    }
    return `Votre accès reste actif jusqu'à la fin du mois en cours. Fin d'accès le ${longDate(context.endOfMonth, false)}.`
}

/** Ce que la carte paie à l'étape 4 : les frais, moins le crédit déduit. */
export function cancelPaymentDueCents(context: CancelPaymentContext): number {
    return context.dueCents ?? context.feesCents ?? 0
}

/** Le récapitulatif de l'étape 4 — `CancelPaymentScreen.js:302-331`. La dernière ligne est le total. */
export function toUICancelPaymentSummary(context: CancelPaymentContext): UICancelSummaryRow[] {
    const isMilestone = isMilestoneCancellation(context.milestoneCancellation)
    const creditCents = context.creditCents ?? 0

    return [
        { label: "Motif", value: cancelReasonLabel(context.motif) },
        isMilestone
            ? { label: "Reprise annulée", value: longDate(context.resumeDate, false) }
            : { label: "Fin de l'abonnement", value: longDate(context.endOfMonth, false) },
        { label: "Frais de résiliation", value: eurosCents(context.feesCents) },
        ...(creditCents > 0 ? [{ label: "Crédit déduit", value: `− ${eurosCents(creditCents)}` }] : []),
        ...((context.balanceDueCents ?? 0) > 0
            ? [{ label: "Solde dû", value: `+ ${eurosCents(context.balanceDueCents)}` }]
            : []),
        { label: "À régler maintenant", value: eurosCents(cancelPaymentDueCents(context)), emphasis: true },
    ]
}

/** La phrase de la confirmation avant prélèvement — `CancelPaymentScreen.js:179`. */
export function cancelPaymentConfirmMessage(context: CancelPaymentContext): string {
    return `Cette action mettra fin à votre abonnement le ${longDate(context.endOfMonth, false)}. Vous pourrez revenir quand vous voulez.`
}

/** Le 402 générique — `CancelPaymentScreen.js:154-157`. */
const DECLINED_FALLBACK = "Le prélèvement des frais a échoué. Vérifiez votre moyen de paiement ou contactez le support."
/** Tout le reste — `CancelPaymentScreen.js:164-167`. */
const GENERIC_FALLBACK = "Une erreur est survenue lors de la résiliation. Veuillez réessayer ou contacter le support."

/**
 * Le seul signal que le backend donne d'une authentification bancaire : le 402 de la 3D Secure
 * porte ce mot dans son message (`CancelSubscriptionController::chargeCancellationFees`, branche
 * « statut non "paid" »). Le legacy ne les distinguait pas — les deux ouvraient « Paiement refusé ».
 */
const THREE_D_SECURE_HINT = /3d\s*secure/i

function errorStatus(error: unknown): number | null {
    if (typeof error === "object" && error !== null && "status" in error) {
        const status = (error as { status?: unknown }).status
        if (typeof status === "number") return status
    }
    return null
}

function errorMessage(error: unknown): string | null {
    if (typeof error === "string" && error.trim()) return error
    if (typeof error === "object" && error !== null && "message" in error) {
        const message = (error as { message?: unknown }).message
        if (typeof message === "string" && message.trim()) return message
    }
    return null
}

/**
 * L'échec de `cancelSubscription`, en état d'étape. Le backend rend **402 dans les deux cas** :
 * le refus de la banque et la 3D Secure demandée. Le message les sépare, et c'est lui qui est
 * affiché — mot pour mot, comme dans le legacy.
 */
export function toUICancelPaymentOutcome(error: unknown): {
    status: UICancelPaymentStatus
    notice: UICancelPaymentNotice
} {
    const message = errorMessage(error)

    if (errorStatus(error) === 402) {
        if (message != null && THREE_D_SECURE_HINT.test(message)) {
            return {
                status: "authenticating",
                notice: { title: "Authentification 3D Secure en cours", message },
            }
        }
        return {
            status: "declined",
            notice: { title: "Paiement refusé", message: message ?? DECLINED_FALLBACK },
        }
    }

    return {
        status: "declined",
        notice: { title: "Erreur", message: message ?? GENERIC_FALLBACK },
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Étape 5 — l'accusé
// ─────────────────────────────────────────────────────────────────────────────

/** Ce que l'étape 5 reçoit, par les paramètres de route (`CancelPaymentScreen.js:140-147`). */
export type CancelGoodbyeContext = {
    paidCents?: number | null
    endOfMonth?: string | null
    milestoneCancellation?: unknown
    resumeDate?: string | null
    parentEmail?: string | null
}

/**
 * On rassure le parent : quelqu'un va le rappeler. Le ticket de suivi churn est bien ouvert côté
 * back (`SelfServeCancellationOpsNotifyJob`) — `CancelGoodbyeScreen.js:80-81`.
 */
const ADVISOR_CALLBACK_CLAUSE =
    "Nous avons bien pris en compte votre demande, un conseiller pédagogique va vous appeler."

/** La phrase de l'accusé — `CancelGoodbyeScreen.js:71-89`, branche `outcome === "cancelled"`. */
export function toUICancelGoodbyeLead(context: CancelGoodbyeContext): string {
    const isMilestone = isMilestoneCancellation(context.milestoneCancellation)
    const paidCents = context.paidCents ?? 0

    const endOfMonthClause = isMilestone
        ? `La reprise prévue le ${longDate(context.resumeDate, false)} est annulée : aucun cours ne sera planifié et aucun prélèvement d'abonnement n'aura lieu.`
        : context.endOfMonth
          ? `Votre abonnement prendra fin le ${longDate(context.endOfMonth, false)} (fin du mois en cours), et votre enfant garde l'accès à ses lives jusque-là.`
          : "Votre abonnement prendra fin à la fin du mois en cours, et votre enfant garde l'accès à ses lives jusque-là."

    if (paidCents > 0) return `${endOfMonthClause} ${ADVISOR_CALLBACK_CLAUSE}`

    return isMilestone
        ? `${endOfMonthClause} Aucun frais à régler — votre reprise n'avait pas commencé. ${ADVISOR_CALLBACK_CLAUSE}`
        : `${endOfMonthClause} Aucun frais de résiliation à régler. ${ADVISOR_CALLBACK_CLAUSE}`
}

/** Le récapitulatif de l'accusé — `CancelGoodbyeScreen.js:164-229`. */
export function toUICancelGoodbyeSummary(context: CancelGoodbyeContext): UICancelSummaryRow[] {
    const rows: UICancelSummaryRow[] = []

    // Un jalon annulé n'a pas de fin d'accès : il n'y avait pas d'accès en cours.
    if (!isMilestoneCancellation(context.milestoneCancellation)) {
        rows.push({ label: "Fin d'accès", value: longDate(context.endOfMonth, false) })
    }
    rows.push({ label: "Frais débités", value: eurosCents(context.paidCents) })
    if (context.parentEmail) {
        rows.push({ label: "Reçu envoyé à", value: context.parentEmail })
    }

    return rows
}
